import {
  Avatar,
  AvatarGroup,
  Button,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useRouter } from 'next/router';
import { FormattedMessage } from 'react-intl';

import { IS_NEW_LINEUP_UI_ENABLED } from 'utils/env.utils';
import { borderedCardHover } from 'utils/styles.utils';

import { useModalControl } from 'hooks/useModalControl';
import { useGenericClick } from 'services/datalayer/browse';
import { PerformerFragment } from 'types/api/dato-operations.types';

import BorderStack from 'components/@common/BorderStack';
import DatoImage from 'components/@common/DatoImage';
import ResponsiveDrawerDialog from 'components/@common/ResponsiveDrawerDialog';
import LineupItem from 'components/@events/LineupItem';
import Icon from 'components/@icons/Icon';

import {
  formatInitialsName,
  formatListOfNames,
  getPerformersCount,
  getPerformersWithFallback,
} from './utils';

interface Props {
  performers: Array<PerformerFragment>;
  slug: string;
  mdlbeastLineupPath: string | undefined;
  performersCount?: number;
}

const LineupItemList = ({
  performers,
  slug,
  mdlbeastLineupPath,
  performersCount,
}: Props) => {
  const handleGenericClick = useGenericClick();
  const { open, onClose, onOpen } = useModalControl();
  const router = useRouter();

  const handleClick = () => {
    handleGenericClick({ clickItem: 'Lineup' });
    if (IS_NEW_LINEUP_UI_ENABLED && mdlbeastLineupPath) {
      router.push(`/events/${slug}/lineup`);
    } else {
      onOpen();
    }
  };

  const performersWithFallback = getPerformersWithFallback(
    performers,
    mdlbeastLineupPath,
  );

  return (
    <>
      <BorderStack
        onClick={handleClick}
        component={Button}
        sx={(theme) => ({
          ...borderedCardHover(theme),
          direction: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          border: `1px solid ${theme.vars.palette.grey[700]}`,
          borderRadius: 4,
          pl: { xs: 1, md: 2 },
          pr: { xs: 2, md: 3 },
          py: 2,
          gap: 2,
        })}
      >
        <AvatarGroup
          total={getPerformersCount(
            mdlbeastLineupPath,
            performers,
            performersCount,
          )}
          max={3}
          // when mdlbeastLineupPath is present and performersCount is not
          // show ... for count as fallback
          {...(IS_NEW_LINEUP_UI_ENABLED &&
          mdlbeastLineupPath &&
          !performersCount
            ? {
                renderSurplus: () => <span>···</span>,
              }
            : {})}
        >
          {performersWithFallback.map((performer) =>
            performer?.background?.url ? (
              <Avatar key={performer.id}>
                <DatoImage
                  data={performer.background}
                  style={{ objectFit: 'cover' }}
                  fill
                  sizes="(max-width: 900px) 64px, 128px"
                />
              </Avatar>
            ) : (
              <Avatar key={performer.id}>
                <Typography
                  variant="small"
                  sx={{
                    fontWeight: 'bold',
                    color: 'text.primary',
                  }}
                >
                  {formatInitialsName(performer.name)}
                </Typography>
              </Avatar>
            ),
          )}
        </AvatarGroup>

        <Typography
          variant="small"
          component="h3"
          sx={{
            fontWeight: 'bold',
            flex: 1,
            pr: 1,
          }}
        >
          {performersWithFallback.map((performer, index) => (
            <span key={performer.id}>
              {formatListOfNames({
                index,
                performers: performersWithFallback,
                performer: performer,
              })}
            </span>
          ))}
        </Typography>

        <Icon name="ChevronRight" rotateInArabic fontSize={24} />
      </BorderStack>
      <ResponsiveDrawerDialog isOpen={open} onClose={onClose}>
        <DialogTitle>
          <FormattedMessage id="event.header.tab.lineup" />
        </DialogTitle>
        <DialogContent>
          <Stack
            sx={{
              gap: 1,
            }}
          >
            {performers.map((performer) => (
              <LineupItem
                key={performer.id}
                performer={performer}
                isOpen={open}
              />
            ))}
          </Stack>
        </DialogContent>
      </ResponsiveDrawerDialog>
    </>
  );
};

export default LineupItemList;
